import { FormRenderProps } from "@progress/kendo-react-form";
import { useCallback, useEffect, useState } from "react";
import { AccumulatorQuoteCard, AccumulatorSubmitSummary } from ".";
import { QuoteExpiry, SubmitDisclaimer } from "..";
import accumulatorApi from "../../../api/accumulator";
import { useApi } from "../../../hooks/useApi";
import { useUserContext } from "../../../hooks/useUserContext";
import { LoadingIndicator } from "../../LoadingIndicator";

interface IAccumulatorSubmitProps {
  formRenderProps: FormRenderProps;
  product: any;
  disableSubmit?: boolean;
}

export const AccumulatorSubmitStep = ({
  formRenderProps,
  product,
  disableSubmit,
}: IAccumulatorSubmitProps) => {
  const { commodity, company } = product;
  const { context: user } = useUserContext();

  const hasSubmitPermission =
    user.permissions.canSubmitQuotes && user.roles.canSubmitQuotes(company.role);
  const hasCredit = company.features?.hasCredit;
  const canSubmit = hasSubmitPermission && hasCredit;

  const getQuoteApi = useApi(accumulatorApi.getQuote);
  const quoteId = formRenderProps.valueGetter("quoteId");
  const quoteName = formRenderProps.valueGetter("quoteName");
  const contract = formRenderProps.valueGetter("contract");
  const volume = formRenderProps.valueGetter("volume");
  const knockOutLevel = formRenderProps.valueGetter("knockOutLevel");
  const strike = formRenderProps.valueGetter("strike");

  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(formRenderProps.valueGetter("quote"));
  const [expiration, setExpiration] = useState<Date | null>(() => {
    const expiryValue = formRenderProps.valueGetter("expiry");
    if (expiryValue && expiryValue !== "") {
      const parsedDate = Date.parse(expiryValue);
      return isNaN(parsedDate) ? null : new Date(parsedDate);
    }
    return null;
  });
  const checkboxFieldName = "isConfirmed";

  const getQuote = useCallback(async () => {
    getQuoteApi.request(quoteId);
  }, [getQuoteApi, quoteId]);

  // Fetch fresh quote if expiration is null
  useEffect(() => {
    if (!expiration && quoteId) {
      getQuote();
    }
  }, [expiration, quoteId, getQuote]);

  useEffect(() => {
    setLoading(getQuoteApi.loading);

    if (getQuoteApi.data) {
      setExpiration(new Date(Date.parse(getQuoteApi.data.quoteExpiry)));
      setQuote(getQuoteApi.data.quote);
      formRenderProps.onChange("quote", { value: getQuoteApi.data.quote });
    }
  }, [getQuoteApi.data, getQuoteApi.loading]);

  const onExpiryChanged = (expiry: string) => {
    formRenderProps.onChange("expiry", { value: expiry });
  };

  const handleAgree = () => {
    formRenderProps.onChange(checkboxFieldName, { value: true });
  };

  return (
    <div className="stepper-form">
      <LoadingIndicator loading={loading || disableSubmit} />

      {!loading && !disableSubmit && (
        <AccumulatorQuoteCard
          title="Quote Summary"
          quote={{ ...quote, knockOutLevel, strike }}
          commodity={commodity}
          showInputs={true}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginTop: "1rem",
        }}
      >
        <div style={{ flexBasis: "50%" }}>
          <SubmitDisclaimer
            canSubmit={canSubmit}
            hasCredit={hasCredit}
            fieldName={checkboxFieldName}
            handleAgree={handleAgree}
          >
            <AccumulatorSubmitSummary
              quoteDetails={{
                quoteId,
                name: quoteName,
                contract,
                volume,
                price: quote.fairValuePrice,
              }}
              commodity={commodity}
              canSubmit={canSubmit}
            />
          </SubmitDisclaimer>
        </div>

        <QuoteExpiry
          expiration={expiration}
          hideExpiry={true}
          loading={loading || disableSubmit}
          onExpire={onExpiryChanged}
          onRefresh={getQuote}
        />
      </div>
    </div>
  );
};
